import type { ClassTabId } from '@/features/class/types';

/** Central React Query key factory — extend per feature as APIs are wired. */
export const queryKeys = {
  examples: {
    all: ['examples'] as const,
  },
  auth: {
    session: ['auth', 'session'] as const,
  },
  feed: {
    global: ['feed', 'global'] as const,
    home: (childStudentInfoId?: number | null) =>
      ['feed', 'global', childStudentInfoId == null ? 'everyone' : childStudentInfoId] as const,
    class: (classCode: string) => ['feed', 'class', classCode] as const,
    profile: (userId: string | number) => ['feed', 'profile', String(userId)] as const,
  },
  explore: {
    /**
     * `GET /userservice/api/v3/user/explore` — "People you may know"
     * suggestions. Scoped by category + filterType + page so the
     * `most/least × ownUniversity/otherUniversity` combos cache
     * independently when filter chips are added later.
     */
    profiles: (
      category: 'most' | 'least',
      filterType: 'ownUniversity' | 'otheruniversity',
      page = 0,
    ) => ['explore', 'profiles', category, filterType, page] as const,
    /** `GET /midl/api/v3/news/explore` — trending posts list. */
    posts: (page = 0, size = 10) => ['explore', 'posts', page, size] as const,
  },
  createPost: {
    /** Audience picker source for the composer — `GET /midl/api/v4/class/data-for-post`. */
    classData: (userId: string | number) => ['create-post', 'class-data', String(userId)] as const,
  },
  messages: {
    conversations: ['messages', 'conversations'] as const,
    thread: (conversationId: number) => ['messages', 'thread', conversationId] as const,
  },
  calendar: {
    global: ['calendar', 'global'] as const,
    class: (classCode: string) => ['calendar', 'class', classCode] as const,
  },
  reportCards: {
    students: ['report-cards', 'students'] as const,
    reports: (studentInfoId: number | string) =>
      ['report-cards', 'reports', String(studentInfoId)] as const,
  },
  payments: {
    overview: ['payments', 'overview'] as const,
    history: ['payments', 'history'] as const,
    status: (referenceId: string) => ['payments', 'status', referenceId] as const,
  },
  class: {
    detail: (classCode: string) => ['class', classCode] as const,
    tab: (classCode: string, tab: ClassTabId) => ['class', classCode, tab] as const,
    roster: (classCode: string) => ['class', classCode, 'roster'] as const,
    members: (classCode: string) => ['class', classCode, 'members'] as const,
    /** `GET /midl/api/v3/class/category` — full named-category list for the profile manage popup. */
    categories: ['class', 'categories'] as const,
    /** `GET /userservice/.../user/myRelative?actionFor=inviteMember` — Add member picker. */
    addableMembers: (classCode: string, query = '') =>
      ['class', classCode, 'addable-members', query] as const,
    institutionDirectory: (classCode: string) =>
      ['class', classCode, 'institution-directory'] as const,
  },
  attendance: {
    sessions: (classCode: string) => ['attendance', classCode, 'sessions'] as const,
    session: (classCode: string, sessionId: number) =>
      ['attendance', classCode, 'session', sessionId] as const,
    /** Period list + setting for a given board+date — drives the "Take attendance" controls. */
    periodSetting: (classCode: string, isoDate: string) =>
      ['attendance', classCode, 'period-setting', isoDate] as const,
    /** Polled live code + counts for an auto-attendance session. */
    autoCode: (classCode: string, configId: number) =>
      ['attendance', classCode, 'auto-code', configId] as const,
    /** Paginated student/parent attendance history. */
    studentHistory: (classCode: string, boardId: string | number) =>
      ['attendance', classCode, 'student-history-v4', String(boardId)] as const,
  },
  behaviour: {
    overview: (classCode: string) => ['behaviour', classCode] as const,
    studentLogs: (
      studentInfoId: number | string,
      nameListId: number | string,
      classId?: number | string | null,
    ) =>
      [
        'behaviour',
        'logs',
        String(studentInfoId),
        String(nameListId),
        classId == null ? 'all-time' : String(classId),
      ] as const,
    /** `GET /midl/api/v3/behavior/team-house/logs/{teamHouseId}` — per-team log history. */
    teamLogs: (teamHouseId: number | string, classId?: number | string | null) =>
      [
        'behaviour',
        'team-logs',
        String(teamHouseId),
        classId == null ? 'all-time' : String(classId),
      ] as const,
    /** `GET /midl/api/v3/behavior/team-student/{boardId}` — Manage Points picker. */
    teamStudentList: (classCode: string, boardId: string | number) =>
      ['behaviour', classCode, 'team-student', String(boardId)] as const,
    /** `GET /midl/api/v3/behavior/category-point` — global category list. */
    pointCategories: ['behaviour', 'point-categories'] as const,
  },
  grades: {
    terms: (classCode: string) => ['grades', classCode, 'terms'] as const,
    detail: (gradeId: number | string) => ['grades', 'detail', String(gradeId)] as const,
    /**
     * `GET /midl/api/v4/grade/{boardId}/{gradeTermId}` — teacher list.
     * Keyed by `(classCode, boardId, gradeTermId)` so the flat board view
     * (`gradeTermId = 1`) and term/category drill-downs cache independently.
     */
    boardList: (classCode: string, boardId: number | string, gradeTermId: number | string = 1) =>
      ['grades', classCode, 'board', String(boardId), 'term', String(gradeTermId)] as const,
    /** `GET /midl/api/v4/grade/member/{boardId}/{gradeTermId}` — student list. */
    studentList: (classCode: string, boardId: number | string, gradeTermId: number | string = 0) =>
      ['grades', classCode, 'member', String(boardId), 'term', String(gradeTermId)] as const,
    /** `GET /midl/api/v3/grade/term/{boardId}` — list of grading terms. */
    termList: (boardId: number | string) => ['grades', 'term-list', String(boardId)] as const,
    questionTermCategories: (gradeTermId: number | string) =>
      ['grades', 'question-term-categories', String(gradeTermId)] as const,
    /** `GET /midl/api/v3/grade/term/category/{termId}/{boardId}` — categories of a term. */
    termCategories: (termId: number | string, boardId: number | string) =>
      ['grades', 'term-categories', String(termId), String(boardId)] as const,
    /** `GET /midl/api/v3/grade/term/year` — academic-year dropdown. */
    termYears: ['grades', 'term-years'] as const,
    /** `GET /midl/api/v3/grade/term/category/all` — global category catalog. */
    allCategories: ['grades', 'all-categories'] as const,
    /** `GET /midl/api/v3/grade/term/detail/{termID}` — prefill for Edit Term. */
    termForEdit: (termId: number | string) => ['grades', 'term-for-edit', String(termId)] as const,
  },
  progress: {
    templates: (classCode: string) => ['progress', classCode, 'templates'] as const,
    reports: (classCode: string) => ['progress', classCode, 'reports'] as const,
    namelist: (classCode: string, reportId: number | string) =>
      ['progress', classCode, 'namelist', String(reportId)] as const,
    userTemplate: (
      classCode: string,
      dpReportId: number | string,
      reportId: number | string,
      namelistEntryId: number | string,
    ) =>
      [
        'progress',
        classCode,
        'user-template',
        String(dpReportId),
        String(reportId),
        String(namelistEntryId),
      ] as const,
  },
  resources: {
    sections: (classCode: string) => ['resources', classCode, 'sections'] as const,
    /** Paginated `GET /board/media` list for a board, scoped by classCode. */
    media: (classCode: string) => ['resources', classCode, 'media'] as const,
    /** Filter chips returned by `GET /board/findMediaType/{boardId}`. */
    mediaTypes: (classCode: string) => ['resources', classCode, 'media-types'] as const,
  },
  profile: {
    current: ['profile', 'current'] as const,
    byId: (userId: string | number) => ['profile', 'user', String(userId)] as const,
    following: (userId: string | number, size = 10) =>
      ['profile', 'user', String(userId), 'following', 'infinite', size] as const,
    followers: (userId: string | number, size = 10) =>
      ['profile', 'user', String(userId), 'followers', 'infinite', size] as const,
    joinedClasses: (userId: string | number, page = 0, size = 10) =>
      ['profile', 'user', String(userId), 'joined-classes', page, size] as const,
  },
  notifications: {
    all: ['notifications'] as const,
    you: ['notifications', 'you'] as const,
    following: ['notifications', 'following'] as const,
    /** `GET /userservice/api/v3/user/follow/request` — pending follow requests. */
    followRequests: (userId: string | number, page = 0, size = 20) =>
      ['notifications', 'follow-requests', String(userId), page, size] as const,
    /** `GET /midl/api/v3/board/join/request` — pending class join requests. */
    joinBoardRequests: (userId: string | number, page = 0, size = 20) =>
      ['notifications', 'join-board-requests', String(userId), page, size] as const,
  },
  search: {
    users: (query: string, page = 0, size = 100) => ['search', 'users', query, page, size] as const,
    classes: (query: string) => ['search', 'classes', query] as const,
  },
  prime: {
    package: (packageId = 1) => ['prime', 'package', packageId] as const,
  },
  tutorPackages: {
    byUsername: (username: string) => ['tutor-packages', 'by-username', username] as const,
    profileByUsername: (username: string) =>
      ['tutor-packages', 'profile-by-username', username] as const,
    confirmation: (referenceId: string) => ['tutor-packages', 'confirmation', referenceId] as const,
    explore: (page = 0) => ['tutor-packages', 'explore', page] as const,
    feed: (page = 0) => ['tutor-packages', 'feed', page] as const,
    directory: ['tutor-packages', 'directory'] as const,
  },
  settings: {
    blockedUsers: ['settings', 'blocked-users'] as const,
    archivedClasses: (userId: string | number) =>
      ['settings', 'archived-classes', String(userId)] as const,
  },
  /**
   * Author-side review dashboard for Question / Assignment posts.
   *   • `list`     → `GET /midl/api/v3/assignment/{assignmentId}/responses?virtualNewsId&page&size`
   *   • `detail`   → `GET /midl/api/v3/assignment/response/{responseId}?virtualNewsId`
   *   • `feedback` → `GET /midl/api/v3/assignment/response/{responseId}/feedback?virtualNewsId&page&size`
   *   • `assignment` → `GET /midl/api/v3/assignment/{assignmentId}?virtualNewsId` (the parent
   *      question row — used by the review page as a hard-refresh hydration
   *      fallback when the local store + React Query feed cache are empty).
   *
   * Keyed by id so each fetch caches independently. The trailing
   * `virtualNewsId` slot keeps the cache per-audience: the same
   * `assignmentId` fanned out to multiple boards has one cache entry
   * per `virtualNewsId` (the Spring controllers treat it as part of
   * the lookup key). `'none'` is used when the caller doesn't know
   * the virtualNewsId yet (legacy mock rows / pre-hydration calls);
   * a future invalidator that wants to nuke "all responses for
   * assignment X regardless of audience" should use a prefix match
   * (`['assignment-responses', 'list', String(assignmentId)]`).
   */
  assignmentResponses: {
    list: (
      assignmentId: number | string,
      page = 0,
      size = 25,
      virtualNewsId?: number | string | null,
    ) =>
      [
        'assignment-responses',
        'list',
        String(assignmentId),
        page,
        size,
        String(virtualNewsId ?? 'none'),
      ] as const,
    detail: (responseId: number | string, virtualNewsId?: number | string | null) =>
      [
        'assignment-responses',
        'detail',
        String(responseId),
        String(virtualNewsId ?? 'none'),
      ] as const,
    feedback: (
      responseId: number | string,
      page = 0,
      size = 25,
      virtualNewsId?: number | string | null,
    ) =>
      [
        'assignment-responses',
        'feedback',
        String(responseId),
        page,
        size,
        String(virtualNewsId ?? 'none'),
      ] as const,
    assignment: (assignmentId: number | string, virtualNewsId?: number | string | null) =>
      [
        'assignment-responses',
        'assignment',
        String(assignmentId),
        String(virtualNewsId ?? 'none'),
      ] as const,
  },
} as const;
